import { createTheme } from '@mui/material/styles';

/**
 * The shared theme for admin React islands.
 *
 * MUI 5 was already a dependency here and unused; MUI 4 is what the older admin
 * components import. New work uses this. Emotion generates scoped class names, so
 * an island themed here cannot collide with the Bootstrap 3 that still styles the
 * surrounding ERB page -- which is what keeps CLAUDE.md's "one framework per
 * component" rule satisfied at the island boundary.
 *
 * ONE look, deliberately: Shoob blue at the admin's normal text size. This briefly
 * carried five colour schemes and four text sizes, each remembered per browser.
 * They came out because a scheme picker can only theme the island -- the nav, the
 * tabs, the heading and every other admin screen are still Bootstrap 3 -- so
 * choosing a dark scheme gave you a dark panel inside a white page. The dark
 * schemes did earn their keep once: they exposed a bug where the island painted no
 * background of its own, which left light text on the white page behind it.
 *
 * Densities and radii are tighter than MUI's defaults: this is a data-entry admin,
 * not a marketing page, and it sits beside Bootstrap 3 tables.
 */
export const buildTheme = () => createTheme({
  palette: {
    mode: 'light',
    primary: { main: '#1976d2' },
    secondary: { main: '#00838f' },
  },
  shape: { borderRadius: 8 },
  typography: {
    // Inherit the admin's own stack rather than importing Roboto, which is not
    // loaded here and would silently fall back anyway.
    fontFamily: 'inherit',
    // Bootstrap 3's scaffolding sets `html { font-size: 10px }`, and MUI's pxToRem
    // assumes 16 -- so every rem size it emitted was landing at 10/16 = 62.5% of
    // what it asked for. Measured: a 13px base rendered body text at 10.7px beside
    // the Bootstrap page's own 14px, which is why the island looked shrunken.
    // Telling MUI the real root size makes px mean px.
    htmlFontSize: 10,
    // Renders body text at ~14.9px, matching the page around it.
    fontSize: 13,
    button: { textTransform: 'none', fontWeight: 600 },
  },
  components: {
    // Dense by default: this sits beside Bootstrap 3 tables and forms.
    MuiTextField: { defaultProps: { size: 'small', fullWidth: true } },
    MuiSelect: { defaultProps: { size: 'small' } },
    MuiButton: { defaultProps: { size: 'small', disableElevation: true } },
    MuiTable: { defaultProps: { size: 'small' } },
    MuiPaper: { defaultProps: { variant: 'outlined' }, styleOverrides: { root: { overflow: 'hidden' } } },
    MuiCheckbox: { defaultProps: { size: 'small' } },
    MuiRadio: { defaultProps: { size: 'small' } },
    MuiChip: { defaultProps: { size: 'small' } },
    // The island lives inside a Bootstrap page; don't let MUI restyle the body.
    MuiCssBaseline: { styleOverrides: { body: { background: 'transparent' } } },
  },
});
